"use client";

import React from "react";
import Link from "next/link";
import { FcAndroidOs } from "react-icons/fc";
import { IoLogoApple } from "react-icons/io";
import { FaReact } from "react-icons/fa";
import { TbDeviceIpad } from "react-icons/tb";
import { MdConnectedTv } from "react-icons/md";
import useTheme from "src/context/appThemeContext";

const NavigationSection = ({ data, leftHeading = "", rightHeading = "" }) => {
  const { theme, mounted } = useTheme();
  const isDark = mounted && theme === "dark";

  const getIconHelper = (title, defaultIcon) => {
    const lower = title.toLowerCase();
    if (lower.includes("android")) {
      return <FcAndroidOs size={32} className="drop-shadow-[0_0_8px_rgba(34,197,94,0.4)]" />;
    }
    if (lower.includes("ios")) {
      return <IoLogoApple size={32} className="text-[#a855f7] drop-shadow-[0_0_8px_rgba(168,85,247,0.4)]" />;
    }
    if (lower.includes("hybrid")) {
      return <FaReact size={32} className="text-[#22d3ee] drop-shadow-[0_0_8px_rgba(34,211,238,0.4)] animate-pulse" />;
    }
    if (lower.includes("ipad")) {
      return <TbDeviceIpad size={32} className="text-[#a855f7] drop-shadow-[0_0_8px_rgba(168,85,247,0.4)]" />;
    }
    if (lower.includes("ott")) {
      return <MdConnectedTv size={32} className="text-[#818cf8] drop-shadow-[0_0_8px_rgba(129,140,248,0.4)]" />;
    }
    return defaultIcon;
  };

  const getPointsForService = (title) => {
    const lower = title.toLowerCase();
    if (lower.includes("android")) {
      return ["Custom Android Solutions", "Optimized UI/UX Design", "Google Play Deployment"];
    }
    if (lower.includes("ios")) {
      return ["Custom iPhone Apps", "Apple-Centric Design", "App Store Publishing"];
    }
    if (lower.includes("hybrid")) {
      return ["Single Codebase Development", "Native App Experience", "Faster Market Launch"];
    }
    if (lower.includes("ipad")) {
      return ["Large Screen Optimization", "Interactive User Interface", "Custom Tablet Layouts"];
    }
    if (lower.includes("ott")) {
      return ["Smart TV Applications", "High-Quality Streaming", "Content Management Integration"];
    }
    return ["Custom App Solutions", "High Performance", "24/7 Ongoing Support"];
  };

  const services = data || [];

  return (
    <div className="mt-10! px-6! bg-background-primary ">
      <div className="flex flex-col items-center text-center mb-10!">
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-lg lg:text-3xl font-medium uppercase tracking-widest block mb-3!">
          WHAT WE BUILD
        </span>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-text-primary mb-6!">
          {leftHeading}
          <span className={`text-transparent bg-clip-text bg-gradient-to-r ${isDark ? "from-cyan-400 to-purple-600" : "from-cyan-600 to-purple-600"}`}>{rightHeading}</span>
        </h2>

        <div className="flex items-center justify-center w-full max-w-md relative">
          <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
          <div className={`absolute w-2.5 h-2.5 rotate-45 border border-purple-500 z-10 ${isDark ? "bg-[#030214]" : "bg-background-primary"}`} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-5! md:mx-10! mx-1!">
        {services.map((item, idx) => (
          <Link
            key={idx}
            href={item.link || "#"}
            className={`relative overflow-hidden rounded-md border backdrop-blur-md transition-all duration-300 group p-8! flex flex-col justify-between min-h-[380px] h-full cursor-pointer ${
              isDark
                ? "border-border-secondary hover:border-cyan-500/30 hover:shadow-[0_0_20px_rgba(34,211,238,0.15)]"
                : "border-border-primary hover:border-cyan-500/50 hover:shadow-[0_10px_30px_rgba(139,92,246,0.1)]"
            }`}
            style={{
              background: "var(--card-background)",
              boxShadow: "var(--card-shadow)",
            }}
          >
            <div>
              <div className="flex items-center gap-4! mb-6!">
                <span
                  className={`inline-flex items-center justify-center p-2.5! rounded-[16px] transition-colors border ${
                    isDark
                      ? "bg-background-primary border-border-primary group-hover:border-cyan-500/30"
                      : "bg-surface-primary border-border-primary group-hover:border-cyan-500/50"
                  }`}
                >
                  {getIconHelper(item.title, item.icon)}
                </span>
                <h3
                  className={`text-lg md:text-xl font-bold transition-colors tracking-tight ${
                    isDark ? "text-white group-hover:text-cyan-400" : "text-text-primary group-hover:text-cyan-600"
                  }`}
                >
                  {item.title}
                </h3>
              </div>

              <p className="text-xs md:text-sm text-text-secondary font-light leading-relaxed mb-6! line-clamp-3">{item.description}</p>

              <ul className="space-y-2.5! mb-8!">
                {(item.points || getPointsForService(item.title)).map((pt, pIdx) => (
                  <li key={pIdx} className="flex items-center gap-2.5! text-xs md:text-sm text-text-secondary font-light">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8b5cf6] shrink-0" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={`text-xs font-semibold transition-colors mt-auto flex items-center gap-1.5! ${
                isDark ? "text-[#8b5cf6] group-hover:text-cyan-400" : "text-[#8b5cf6] group-hover:text-cyan-600"
              }`}
            >
              <span>Learn More</span>
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default NavigationSection;
